import { computed, inject, Injectable, resource } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SHARED_LINK_PARAM } from '../../core/shared-link';
import { debugLog } from '../../helpers/debugLog';
import { locatorSafeSerialize } from '../../helpers/locator';
import { getLocatorContextAndReadingPoint } from '../../helpers/readium';
import { isHighlight } from '../../helpers/selections';
import type { LocatorLike, Plugin } from '../../models/metadata-response';
import type {
  Highlight,
  Selection,
  SelectionResponse,
  SelectionSave,
} from '../../models/selections';
import { FeatureFlagsService } from '../feature-flags/feature-flags';
import { MetadataService } from '../metadata/metadata';
import { ReaderApi } from '../reader-api/reader-api';
import { BookAnalysisService } from '../readium/book-analysis';
import { ChapterService } from '../readium/chapter/chapter';
import { ReaderNavigatorService } from '../readium/navigator/reader-navigator';
import { ToastService } from '../toast/toast';

const SELECTION_PLUGINS: Plugin[] = ['highlights', 'notes', 'bookmarks'];

@Injectable({
  providedIn: 'root',
})
export class SelectionsService {
  private readonly readerApi = inject(ReaderApi);
  private readonly readerNavigatorService = inject(ReaderNavigatorService);
  private readonly metadataService = inject(MetadataService);
  private readonly chapterService = inject(ChapterService);
  private readonly bookAnalysis = inject(BookAnalysisService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly featureFlags = inject(FeatureFlagsService);

  private readonly hasAnySelectionPlugin = computed(() =>
    SELECTION_PLUGINS.some((plugin) => this.featureFlags.isEnabled(plugin)()),
  );

  private readonly selectionsResource = resource({
    params: () => this.metadataService.metadata(),
    loader: async ({ params }) => {
      if (!this.hasAnySelectionPlugin()) return [];
      try {
        return await this.readerApi.getSelectionsResource(params.token);
      } catch (error) {
        console.error('Error loading selections:', error);
        return [];
      }
    },
  });
  readonly selections = computed(() => {
    const base = this.selectionsResource.hasValue()
      ? this.selectionsResource.value().map<Selection>(this.extendSelection.bind(this))
      : [];
    const shared = this.metadataService.sharedSelection();
    if (!shared || base.some((selection) => selection.id === shared.id)) return base;
    const extendedShared = this.extendSelection(shared);
    if (!isHighlight(extendedShared)) return base;
    // Recipients see it in its own color (per design), whatever the owner picked. A note the
    // owner didn't make public is never shown.
    const sharedHighlight: Highlight = {
      ...extendedShared,
      color: 'highlight-shared',
      notes: extendedShared.publicNotes === false ? undefined : extendedShared.notes,
      isShared: true,
    };
    return [...base, sharedHighlight];
  });

  /** Id of the selection this session was opened for via a shared link, if any. */
  readonly sharedSelectionId = computed(() => this.metadataService.sharedSelection()?.id);

  readonly selectionContext = this.readerNavigatorService.currentSelectionContext;

  readonly actualLocator = this.readerNavigatorService.actualLocator;

  async saveSelection(selection: SelectionSave): Promise<Selection | undefined> {
    const locator = this.actualLocator();
    if (!locator) return undefined;
    const { text, contextReadingPoint } = getLocatorContextAndReadingPoint(
      this.readerNavigatorService.getCurrentFrameElements(),
      this.selectionContext(),
    );

    const params = {
      locator: { ...locatorSafeSerialize(locator), text },
      ...selection,
      contextReadingPoint,
      idRef: this.metadataService.componentIdFor(locator.href),
      progress: this.bookAnalysis.calculateProgressFromLocator(locator),
    };

    const selectionResponse = await this.readerApi.saveSelection(params);
    this.selectionsResource.update((selections) => [...(selections ?? []), selectionResponse]);
    return this.selections().find((sel) => sel.id === selectionResponse.id);
  }

  async updateSelection(selectionId: string, selectionToUpdate: SelectionSave): Promise<void> {
    const updatedSelection = await this.readerApi.updateSelection(selectionId, selectionToUpdate);
    this.selectionsResource.update((selections) =>
      selections?.map((selection) => (selection.id === selectionId ? updatedSelection : selection)),
    );
  }

  async removeSelection(selectionId: string): Promise<void> {
    await this.readerApi.removeSelection(selectionId);
    this.selectionsResource.update((selections) =>
      selections?.filter((selection) => selection.id !== selectionId),
    );
  }

  /**
   * Copies the share link of a highlight to the clipboard. Persists the `sharedId` (generated on
   * first share) and `publicNotes` (whether the note is shared along) when either changes.
   */
  async shareSelectionLink(
    selection: Highlight,
    options: { publicNotes?: boolean } = {},
  ): Promise<void> {
    const sharedId = selection.sharedId ?? crypto.randomUUID();
    const publicNotes = options.publicNotes ?? selection.publicNotes;
    if (sharedId !== selection.sharedId || publicNotes !== selection.publicNotes) {
      try {
        await this.updateSelection(selection.id, { ...selection, sharedId, publicNotes });
      } catch (error) {
        debugLog('Error persisting shared selection id:', error);
        this.toastService.error(
          String(this.translateService.instant('TEXT_SELECTION.ERROR.SHARED_SELECTION')),
        );
        return;
      }
      // Callers may keep holding this object (e.g. noteToEdit): a re-share must reuse the same id,
      // or the link copied before would stop resolving.
      Object.assign(selection, { sharedId, publicNotes });
    }
    const { bookId, otkDomain } = this.metadataService.otkConfig();
    const url = `${otkDomain}/info/${bookId}?${SHARED_LINK_PARAM}=${sharedId}`;
    try {
      await navigator.clipboard.writeText(url);
      this.toastService.success(String(this.translateService.instant('DIALOG.SHARE.LINK_COPIED')));
    } catch (error) {
      debugLog('Error copying link to clipboard:', error);
      this.toastService.error(String(this.translateService.instant('TEXT_SELECTION.ERROR.COPY')));
    }
  }

  async sendClubComment(selection: Highlight): Promise<void> {
    try {
      await this.readerApi.sendClubComment({
        highlight: selection.selectedText,
        text: selection.notes ?? '',
        ebookReadingPercentage: selection.progress,
      });
      this.toastService.success(
        String(this.translateService.instant('TEXT_SELECTION.SEND_TO_CLUB_OK')),
      );
    } catch (error) {
      debugLog('Error sending comment to club:', error);
      this.toastService.error(
        String(this.translateService.instant('TEXT_SELECTION.ERROR.SEND_TO_CLUB')),
      );
    }
  }

  extendSelection(selection: SelectionResponse): Selection {
    let href = '';
    let locator: LocatorLike | undefined = undefined;
    if ('locator' in selection) {
      ({ locator } = selection);
      ({ href } = locator);
    } else {
      href = this.chapterService.findHrefByIdRef(selection.idRef) ?? '';
      locator = this.bookAnalysis.getLocatorForPercentage(
        selection.progress,
        'selectedText' in selection ? selection.selectedText : undefined,
      )!;
    }
    return {
      ...selection,
      chapterTitle: this.chapterService.getTitleFromHref(href),
      href,
      locator,
    };
  }
}
